import { DateTime } from 'luxon';
import html2canvas from 'html2canvas';
import { mapInstance } from './state.js';

export function ready(fn) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn);
}

export function formatTrackDateRange(startIso, endIso, time_zone) {
  if (!time_zone || ['utc', 'gmt'].includes(time_zone.toLowerCase())) {
    time_zone = 'UTC';
  }
  
  const start = DateTime.fromISO(startIso, { zone: time_zone });
  const end = DateTime.fromISO(endIso, { zone: time_zone });

  if (start.hasSame(end, "day")) {
    return start.toLocaleString(DateTime.DATE_FULL);
  } else {
    return `${start.toLocaleString(DateTime.DATE_FULL)} &mdash; ${end.toLocaleString(DateTime.DATE_FULL)}`;
  }
}

/**
 * Fetches timezone name from a public API using lat/lon.
 * Falls back to UTC if it fails.
 */
export async function getTimezoneFromLatLon(lat, lon) {
    try {
        const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`);
        const data = await response.json();
        return data.timeZone || 'UTC';
    } catch (error) {
        console.error('Error fetching timezone:', error);
        return 'UTC';
    }
}

export function inchesToFeetInches(totalInches) {
  if (!totalInches || isNaN(totalInches) || totalInches <= 0) return '';
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  let result = '';
  if (feet > 0) result += `${feet}'`;
  if (inches > 0) result += `${inches}"`;
  return result;
}

export function isValidFeetInchesFormat(value) {
  if (!value) return true;

  const regex = /^(\d+(\.\d+)?)?'(\d+)"?$/;
  const regexFeetOnly = /^(\d+(\.\d+)?)'$/;
  const regexInchesOnly = /^(\d+)"$/;
  const regexCombinedNoQuote = /^(\d+(\.\d+)?)\s+(\d+)$/;
  const regexNumberOnly = /^\d+(\.\d+)?$/;

  return regex.test(value) || regexFeetOnly.test(value) || regexInchesOnly.test(value) || regexCombinedNoQuote.test(value) || regexNumberOnly.test(value);
}

export function feetInchesToInches(str) {
  if (!str || typeof str !== 'string') return 0;

  // Handle single number as feet
  if (/^\d+(\.\d+)?$/.test(str.trim())) {
    const feet = parseFloat(str.trim());
    return Math.round(feet * 12);
  }

  const feetMatch = str.match(/(\d+(?:\.\d+)?)\s*'/);
  const inchMatch = str.match(/(\d+)\s*"/);
  const feet = feetMatch ? parseFloat(feetMatch[1]) : 0;
  const inches = inchMatch ? parseInt(inchMatch[1], 10) : 0;

  if (!feetMatch && !inchMatch && /^\d+(?:\.\d+)?\s+\d+$/.test(str.trim())) {
    const parts = str.trim().split(/\s+/);
    return Math.round(parseFloat(parts[0]) * 12 + parseInt(parts[1], 10));
  }

  return Math.round(feet * 12 + inches);
}

export function degrees(d) {
  d = (d % 360 + 360) % 360;

  if (d > 348 || d <= 11) return "N";
  if (d <= 33) return "N by NE";
  if (d <= 56) return "NE";
  if (d <= 78) return "E by NE";
  if (d <= 101) return "E";
  if (d <= 123) return "E by SE";
  if (d <= 146) return "SE";
  if (d <= 168) return "S by SE";
  if (d <= 191) return "S";
  if (d <= 213) return "S by SW";
  if (d <= 236) return "SW";
  if (d <= 258) return "W by SW";
  if (d <= 281) return "W";
  if (d <= 303) return "W by NW";
  if (d <= 326) return "NW";
  if (d <= 348) return "N by NW";
  return "";
}

export function getIconForWeather(desc) {
  let icon = 'cloud';
  const lowerDesc = desc.toLowerCase();

  if (lowerDesc.includes('clear')) icon = 'clear_day';
  else if (lowerDesc.includes('partly cloudy')) icon = 'partly_cloudy_day';
  else if (lowerDesc.includes('overcast')) icon = 'cloud';
  else if (lowerDesc.includes('drizzle')) icon = 'weather_mix';
  else if (lowerDesc.includes('rain')) icon = 'rainy';
  
  return icon;
}

export function countDecimalPlaces(number) {
    const numberString = number.toString();
    const parts = numberString.split('.');
  
    if (parts.length === 2) {
      return parts[1].length;
    } else {
      return 0; // No decimal part
    }
  }


  /**
 * Captures the main app content and downloads it as a PNG.
 */
export function captureAndExport() {


    const hidelist = [".track-actions", ".tracks-panel", ".auth-controls", ".mapboxgl-control-container", ".legend-toolbar", ".share-trip-btn", ".legend-track-color-picker"];
    let domlist = [];

    hidelist.forEach(selector => {
        const domCompList = document.querySelectorAll(selector);

        domCompList.forEach(domComp => {
          if (domComp) {
            if (domComp.classList.contains("legend-track-color-picker")){
              domComp.style.backgroundColor = domComp.value;
              domComp.style.color = domComp.value;
              return
            } 
            domComp.classList.add('hidden');
            domlist.push(domComp);
          };  
        });
        
    });

    const layers = mapInstance.getStyle().layers;
    let layersList = [];

    layers.forEach(layer => {
        if (layer.id.includes("photos-layer-") || layer.id.includes("photos-circle-layer-") || layer.id.includes("notes-layer-") || layer.id.includes("notes-circle-layer") ){
            mapInstance.setLayoutProperty(layer.id, 'visibility', 'none');
            layersList.push(layer.id);
        }
    });

    mapInstance.setZoom(mapInstance.getZoom());


    mapInstance.once('render', () => {
        // Now that the map has re-drawn, it's safe to capture.
        html2canvas(document.body, {
            useCORS: true, 
            allowTaint: true,
            onclone: (clonedDoc) => {
                // Strip box-shadows: html2canvas renders them as solid dark rectangles.
                const noShadow = clonedDoc.createElement('style');
                noShadow.textContent = '* { box-shadow: none !important; }';
                clonedDoc.head.appendChild(noShadow);

                const mapCanvas = clonedDoc.querySelector('.mapboxgl-canvas');
                if (mapCanvas) {
                    mapCanvas.style.transform = 'none';
                }
            }
        }).then(canvas => {
            // Create a link to download the image
            const link = document.createElement('a');
            link.download = 'wakelog-capture.png';
            link.href = canvas.toDataURL('image/png');
            link.click();

            domlist.forEach(domComp => {
                if (domComp) domComp.classList.remove('hidden');
            });

            layersList.forEach(layerid => {
                mapInstance.setLayoutProperty(layerid, 'visibility', 'visible');
            });
        });
    });



}